import {
  Button,
  Card,
  Input,
  Label,
  SYMPTOM_OPTIONS,
  Switch,
  Textarea,
} from "@nfp-tracker/components";
import type { Database } from "@nfp-tracker/utils";
import {
  addDaysToDateKey,
  analyzeCycle,
  getCyclePhases,
  BLEEDING_OPTIONS,
  buildLegacyMucusSummary,
  getCurrentUserTimeZone,
  getDateKeyInTimeZone,
  getFertilityAdviceDisplay,
  MUCUS_COLOR_OPTIONS,
  MUCUS_CONSISTENCY_OPTIONS,
  MUCUS_CONSISTENCY_OPTIONS_BASIC,
  MUCUS_FREQUENCY_OPTIONS,
  normalizeOvulationTest,
  normalizeTemperatureInput,
  normalizeTimeZone,
  resolveLegacyMucus,
  usesExpandedMucus,
  usesMonitor,
  usesSymptoThermal,
} from "@nfp-tracker/utils";
import type { NFPMethod, OvulationTestResult } from "@nfp-tracker/utils";
import {
  Activity,
  Coffee,
  Droplets,
  FlaskConical,
  Heart,
  Thermometer,
  Zap,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { addDays, differenceInCalendarDays, format, parseISO } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { GettingStartedCard } from "../components/GettingStartedCard";
import { LoadingSkeleton } from "../components/LoadingSkeleton";
import { SubscriptionGate } from "../components/SubscriptionGate";
import TrackingGuide from "../components/TrackingGuide";
import WelcomeGuide from "../components/WelcomeGuide";
import { useToast } from "@nfp-tracker/components";
import { ROUTES } from "../constants/routes";
import {
  mergeObservationRow,
  observationsQueryKey,
  useObservationsCache,
} from "../hooks/useObservationsCache";
import { useOwnerContext, type Couple } from "../hooks/useOwnerContext";
import { captureException, trackEvent } from "../integrations/analytics/client";
import { supabase } from "../integrations/supabase/client";
import { getCycleForDate } from "../utils/derivedCycles";

type MonitorValue = "LOW" | "HIGH" | "PEAK" | null;
type Cycle = {
  id: string;
  start_date: string;
  end_date: string | null;
  predicted_avoid_start_cd: number | null;
  first_peak_cd: number | null;
  post_peak_progress: string | null;
  /** No recorded cycle start covers this date yet, so its cycle day is unknown. */
  start_inferred: boolean;
};
type ObservationDraft = {
  bleeding: number | null;
  isCycleStart: boolean;
  monitor: MonitorValue;
  ovulationTest: OvulationTestResult | null;
  intercourse: boolean | null;
  mucus: string;
  mucusColor: string;
  mucusFrequency: string;
  notes: string;
  symptoms: string[];
  temperature: string;
};

const LAST_SAVED_EVENT = "observations-last-saved-updated";

// Autosave pacing. Discrete picks (taps on bleeding/mucus/symptoms) settle quickly; free-text
// typing gets a longer quiet window so a sentence becomes one write instead of one per word.
// AUTOSAVE_MAX_WAIT_MS caps how long a continuous typer can go unsaved — without it, someone
// typing steadily for a minute would never cross the quiet threshold and never persist.
const AUTOSAVE_DEBOUNCE_MS = 800;
const AUTOSAVE_TEXT_DEBOUNCE_MS = 1500;
const AUTOSAVE_MAX_WAIT_MS = 5000;

const getDraftSnapshot = (draft: ObservationDraft): string =>
  JSON.stringify({
    ...draft,
    symptoms: [...draft.symptoms].sort(),
  });

const parseDateParam = (value: string | null): string | null => {
  if (!value) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;

  const parsed = parseISO(value);
  return Number.isNaN(parsed.getTime()) ? null : value;
};

/**
 * Analytics props for an observation save. Deliberately generic: only how many fields were
 * filled in, never which ones (bleeding, intercourse, temperature, cycle day, date, …) —
 * those are health data and must not reach PostHog.
 */
const buildObservationEventProps = (draft: ObservationDraft, mode: "create" | "update") => ({
  mode,
  filledFieldCount: [
    draft.bleeding != null,
    draft.isCycleStart,
    Boolean(draft.monitor),
    draft.ovulationTest != null,
    draft.intercourse != null,
    Boolean(draft.mucus || draft.mucusColor || draft.mucusFrequency),
    Boolean(draft.notes.trim()),
    draft.symptoms.length > 0,
    Boolean(normalizeTemperatureInput(draft.temperature)),
  ].filter(Boolean).length,
});

const Observations = () => {
  const [cycle, setCycle] = useState<Cycle | null>(null);
  const [cycleDay, setCycleDay] = useState(1);
  const [bleeding, setBleeding] = useState<number | null>(null);
  const [isCycleStart, setIsCycleStart] = useState(false);
  const [monitor, setMonitor] = useState<MonitorValue>(null);
  const [ovulationTest, setOvulationTest] = useState<OvulationTestResult | null>(null);
  const [intercourse, setIntercourse] = useState<boolean | null>(null);
  const [mucus, setMucus] = useState("");
  const [mucusColor, setMucusColor] = useState("");
  const [mucusFrequency, setMucusFrequency] = useState("");
  const [notes, setNotes] = useState("");
  const [symptoms, setSymptoms] = useState<string[]>([]);
  const [temperature, setTemperature] = useState<string>("");
  const [temperatureToPersist, setTemperatureToPersist] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [isTouchOnlyDevice, setIsTouchOnlyDevice] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dateParam = searchParams.get("date");
  const { toast } = useToast();
  const [viewedDate, setViewedDate] = useState(
    getDateKeyInTimeZone(new Date(), getCurrentUserTimeZone())
  );
  const [isViewingToday, setIsViewingToday] = useState(true);
  const draftRef = useRef<ObservationDraft | null>(null);
  const lastSavedSnapshotRef = useRef<string | null>(null);
  const autosaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipNextAutosaveRef = useRef(false);
  const saveInFlightRef = useRef(false);
  const pendingSaveDraftRef = useRef<ObservationDraft | null>(null);
  const pendingSaveDateRef = useRef<string | null>(null);
  const previousTargetDateRef = useRef<string | null>(null);
  // The date draftRef belongs to. A flush can happen after the user has already navigated to
  // another day, so the write has to carry the date the draft was authored for.
  const draftDateRef = useRef<string | null>(null);
  // When the current run of unsaved edits started, for the max-wait cap.
  const pendingSinceRef = useRef<number | null>(null);
  const lastAutosaveNotesRef = useRef("");
  // Assigned below once flushPendingSave exists; held in a ref so the exit-path listeners and
  // the day-derivation effect can call the current flush without resubscribing or widening
  // their dependency lists.
  const flushPendingSaveRef = useRef<() => void>(() => {});
  const viewedDateRef = useRef<string | null>(null);

  const queryClient = useQueryClient();
  // Shared, session-cached auth/owner/permission context — repeat navigation between pages
  // no longer re-runs the auth+onboarding+owner-resolution chain from scratch each time.
  const ownerContextQuery = useOwnerContext();
  const ownerContext = ownerContextQuery.data;
  const couple = ownerContext?.status === "ready" ? ownerContext.couple : null;
  const canWriteObservations = ownerContext?.status === "ready" ? ownerContext.canWriteObservations : true;
  const isPostpartum = ownerContext?.status === "ready" ? ownerContext.isPostpartum : false;
  const optionalFields = couple?.optional_fields || {
    temperature: true,
    mucus: true,
    monitor: true,
    bleeding: true,
    symptoms: true,
    notes: true,
  };

  // Shared cache (~13 months / ~12 cycles), kept fresh by the single realtime subscription
  // mounted in AppShellLayout. Switching days no longer refetches or re-derives anything
  // from the network — it's a synchronous read from data that's already loaded.
  const cache = useObservationsCache(couple?.id, couple?.methods);
  const loading = ownerContextQuery.isLoading || cache.isLoading;

  const emitLastSaved = (iso: string | null) => {
    window.dispatchEvent(new CustomEvent<string | null>(LAST_SAVED_EVENT, { detail: iso }));
  };

  const buildDraftFromState = useCallback(
    (): ObservationDraft => ({
      bleeding,
      isCycleStart,
      monitor,
      ovulationTest,
      intercourse,
      mucus,
      mucusColor,
      mucusFrequency,
      notes,
      symptoms,
      temperature: temperatureToPersist,
    }),
    [
      bleeding,
      isCycleStart,
      monitor,
      ovulationTest,
      intercourse,
      mucus,
      mucusColor,
      mucusFrequency,
      notes,
      symptoms,
      temperatureToPersist,
    ]
  );

  const applyDraftToState = (draft: ObservationDraft) => {
    lastAutosaveNotesRef.current = draft.notes;
    pendingSinceRef.current = null;
    setBleeding(draft.bleeding);
    setIsCycleStart(draft.isCycleStart);
    setMonitor(draft.monitor);
    setOvulationTest(draft.ovulationTest);
    setIntercourse(draft.intercourse);
    setMucus(draft.mucus);
    setMucusColor(draft.mucusColor);
    setMucusFrequency(draft.mucusFrequency);
    setNotes(draft.notes);
    setSymptoms(draft.symptoms);
    const normalizedTemperature = normalizeTemperatureInput(draft.temperature);
    setTemperature(normalizedTemperature || draft.temperature);
    setTemperatureToPersist(normalizedTemperature);
  };

  // Check if first time user and show welcome guide
  useEffect(() => {
    const hasSeenWelcome = localStorage.getItem("hasSeenWelcome");

    if (!hasSeenWelcome && couple) {
      setShowWelcome(true);
    }
  }, [couple]);

  const dismissWelcome = () => {
    localStorage.setItem("hasSeenWelcome", "true");
    setShowWelcome(false);
  };

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return;
    }

    const hoverQuery = window.matchMedia("(hover: hover)");
    const pointerFineQuery = window.matchMedia("(pointer: fine)");

    const updateTouchOnly = () => {
      setIsTouchOnlyDevice(!hoverQuery.matches && !pointerFineQuery.matches);
    };

    updateTouchOnly();
    hoverQuery.addEventListener("change", updateTouchOnly);
    pointerFineQuery.addEventListener("change", updateTouchOnly);

    return () => {
      hoverQuery.removeEventListener("change", updateTouchOnly);
      pointerFineQuery.removeEventListener("change", updateTouchOnly);
    };
  }, []);

  // Auth/onboarding redirects react to the shared cached owner context rather than doing
  // their own fetch — see useOwnerContext for the actual resolution chain.
  useEffect(() => {
    if (ownerContext?.status === "unauthenticated") {
      navigate(ROUTES.auth);
    } else if (ownerContext?.status === "needs-onboarding") {
      navigate(ROUTES.onboarding);
    }
  }, [ownerContext, navigate]);

  useEffect(() => {
    if (!ownerContextQuery.error) return;
    captureException(ownerContextQuery.error, { source: "observations_owner_load" });
    toast({
      title: "Error",
      description: (ownerContextQuery.error as Error).message,
      variant: "destructive",
    });
  }, [ownerContextQuery.error, toast]);

  // Derives the viewed day's draft from the shared cache. Runs on genuine day-navigation
  // (dateParam change) and whenever the cache itself changes (our own save, a spouse's
  // edit, etc.) — but only navigation unconditionally applies the incoming draft. A
  // same-day cache change while there's a genuine unsaved local edit (or a save actively in
  // flight) is skipped for the edit fields specifically, so it can't silently revert
  // whatever the user is in the middle of typing/clicking.
  useEffect(() => {
    if (!couple || cache.isLoading) return;

    const effectiveTimeZone = normalizeTimeZone(couple.timezone);
    const todayStr = getDateKeyInTimeZone(new Date(), effectiveTimeZone);

    const requestedDateParam = parseDateParam(dateParam);
    let targetDate = requestedDateParam ?? todayStr;
    if (differenceInCalendarDays(parseISO(targetDate), parseISO(todayStr)) > 0) {
      targetDate = todayStr;
    }

    const previousTargetDate = previousTargetDateRef.current;
    const isNavigation = previousTargetDate !== targetDate;
    previousTargetDateRef.current = targetDate;

    // Navigation unconditionally replaces the draft below, so an edit still sitting in the
    // debounce window for the day we're leaving has to be written out first — otherwise the
    // pending timer is just cleared and those keystrokes are gone.
    if (isNavigation && previousTargetDate !== null) {
      flushPendingSaveRef.current();
    }

    setViewedDate(targetDate);
    viewedDateRef.current = targetDate;
    setIsViewingToday(targetDate === todayStr);

    const expectedDateParam = targetDate === todayStr ? null : targetDate;
    if (dateParam !== expectedDateParam) {
      setSearchParams(expectedDateParam ? { date: expectedDateParam } : {}, { replace: true });
    }

    const cycleData = getCycleForDate(cache.cycles, targetDate, todayStr);

    const hasPendingLocalEdit =
      !isNavigation &&
      (saveInFlightRef.current ||
        (draftRef.current !== null &&
          getDraftSnapshot(draftRef.current) !== lastSavedSnapshotRef.current));

    const setCycleStable = (next: Cycle) =>
      setCycle((prev) => (prev && JSON.stringify(prev) === JSON.stringify(next) ? prev : next));

    if (!cycleData) {
      // Nothing logged on or before this date: it isn't day 1 of anything until the user
      // marks it as a cycle start, so the cycle day is treated as unknown.
      const fallbackCycle: Cycle = {
        id: `cycle-${targetDate}`,
        start_date: targetDate,
        end_date: null,
        predicted_avoid_start_cd: null,
        first_peak_cd: null,
        post_peak_progress: null,
        start_inferred: true,
      };
      setCycleStable(fallbackCycle);
      setCycleDay(1);

      if (!hasPendingLocalEdit) {
        const initialDraft: ObservationDraft = {
          bleeding: null,
          isCycleStart: false,
          monitor: null,
          ovulationTest: null,
          intercourse: null,
          mucus: "",
          mucusColor: "",
          mucusFrequency: "",
          notes: "",
          symptoms: [],
          temperature: "",
        };
        applyDraftToState(initialDraft);
        draftRef.current = initialDraft;
        draftDateRef.current = targetDate;
        lastSavedSnapshotRef.current = getDraftSnapshot(initialDraft);
        emitLastSaved(null);
      }
      return;
    }

    setCycleStable(cycleData as Cycle);

    const diffDays = differenceInCalendarDays(
      parseISO(targetDate),
      parseISO(cycleData.start_date)
    );
    setCycleDay(Math.max(1, diffDays + 1));

    if (hasPendingLocalEdit) return;

    const selectedDayObs = cache.observations.find(
      (o) => o.cycle_id === cycleData.id && o.observation_date === targetDate
    );

    const selectedDayDraft: ObservationDraft = selectedDayObs
        ? {
            bleeding: selectedDayObs.bleeding,
            isCycleStart: selectedDayObs.is_cycle_start,
            monitor: selectedDayObs.monitor as MonitorValue,
            ovulationTest: normalizeOvulationTest(selectedDayObs.ovulation_test),
            intercourse: selectedDayObs.intercourse ?? null,
            mucus:
              selectedDayObs.mucus_consistency ||
              resolveLegacyMucus(selectedDayObs.mucus, couple.methods).consistency,
            mucusColor:
              selectedDayObs.mucus_color ||
              resolveLegacyMucus(selectedDayObs.mucus, couple.methods).color,
          mucusFrequency: selectedDayObs.mucus_frequency || "",
          notes: selectedDayObs.notes || "",
          symptoms: selectedDayObs.symptoms || [],
          temperature: selectedDayObs.temperature_f?.toString() || "",
        }
        : {
            bleeding: null,
            isCycleStart: false,
            monitor: null,
            ovulationTest: null,
            intercourse: null,
            mucus: "",
            mucusColor: "",
            mucusFrequency: "",
          notes: "",
          symptoms: [],
          temperature: "",
        };

    applyDraftToState(selectedDayDraft);
    draftRef.current = selectedDayDraft;
    draftDateRef.current = targetDate;
    lastSavedSnapshotRef.current = getDraftSnapshot(selectedDayDraft);
    emitLastSaved(selectedDayObs?.updated_at || selectedDayObs?.created_at || null);
  }, [couple, cache.isLoading, cache.cycles, cache.observations, dateParam, setSearchParams]);

  // Observations belonging to the currently viewed cycle, derived straight from the shared
  // cache — no separate fetch or local copy to keep in sync.
  const allObservations = useMemo(
    () => (cycle ? cache.observations.filter((obs) => obs.cycle_id === cycle.id) : []),
    [cache.observations, cycle]
  );

  // entryDate is passed explicitly rather than read from viewedDate: a flush triggered by day
  // navigation, unmount, or the tab being hidden can run after viewedDate has already moved on,
  // and the pending edit must still land on the day it was typed for.
  const saveDraft = useCallback(async (draft: ObservationDraft, entryDate: string) => {
    if (!canWriteObservations) return;
    if (!couple || !cycle) {
      captureException(new Error("saveDraft called without couple or cycle"), {
        source: "observation_save_guard",
        hasCouple: Boolean(couple),
        hasCycle: Boolean(cycle),
      });
      return;
    }

    setSaving(true);
    saveInFlightRef.current = true;
    let saveMode: "create" | "update" = "update";
    // Snapshot actually persisted by this call, used to decide whether a queued edit is still
    // meaningfully different. Stays null on failure so the queued edit retries.
    let savedSnapshot: string | null = null;
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      const normalizedTemperature = normalizeTemperatureInput(draft.temperature);
      // Color is only ever set via the Creighton/Billings picker. If the couple has since
      // switched to a method that doesn't show a color picker, don't carry forward a color
      // left over from before the switch — it'd otherwise silently re-save alongside
      // whatever consistency value the current method's (different) picker writes.
      const isExpandedMucusMethod = usesExpandedMucus(couple.methods);
      const mucusColorToSave = isExpandedMucusMethod ? draft.mucusColor : "";
      const fields = {
        owner_user_id: couple.id,
        observation_date: entryDate,
        is_cycle_start: draft.isCycleStart,
        bleeding: draft.bleeding,
        monitor: draft.monitor,
        ovulation_test: draft.ovulationTest,
        intercourse: draft.intercourse,
        mucus: buildLegacyMucusSummary(draft.mucus, mucusColorToSave),
        mucus_consistency: draft.mucus || null,
        mucus_color: mucusColorToSave || null,
        mucus_frequency: draft.mucusFrequency || null,
        notes: draft.notes || null,
        symptoms: draft.symptoms.length > 0 ? draft.symptoms : null,
        temperature_f: normalizedTemperature ? Number.parseFloat(normalizedTemperature) : null,
        created_by_user_id: user?.id,
      };

      // Check if an observation already exists for today
      const { data: existingObs, error: existingError } = await supabase
        .from("observations")
        .select("id, is_cycle_start")
        .eq("owner_user_id", couple.id)
        .eq("observation_date", entryDate)
        .maybeSingle();

      if (existingError) {
        console.error("Error checking existing observation:", existingError);
        throw existingError;
      }

      let savedObs;
      let obsError;
      const wasInsert = !existingObs;
      saveMode = wasInsert ? "create" : "update";

      if (existingObs) {
        // Update existing observation
        const { data, error } = await supabase
          .from("observations")
          .update(fields)
          .eq("id", existingObs.id)
          .select()
          .maybeSingle();
        savedObs = data;
        obsError = error;
      } else {
        // Insert new observation
        const { data, error } = await supabase
          .from("observations")
          .insert(fields)
          .select()
          .maybeSingle();
        savedObs = data;
        obsError = error;
      }

      if (obsError) {
        console.error("Error saving observation:", obsError);
        throw obsError;
      }

      // Some writes can succeed but return 0 rows; re-read by owner/date before failing.
      if (!savedObs) {
        const { data: persistedObs, error: persistedObsError } = await supabase
          .from("observations")
          .select("*")
          .eq("owner_user_id", couple.id)
          .eq("observation_date", entryDate)
          .maybeSingle();

        if (persistedObsError) {
          console.error("Error reading persisted observation:", persistedObsError);
          throw persistedObsError;
        }

        savedObs = persistedObs;
      }

      if (!savedObs) {
        throw new Error("Observation save returned no row. Please try again.");
      }

      // Push the confirmed row straight into the shared cache instead of patching local
      // state by hand — the day-derivation effect reacts to the cache changing and
      // re-derives cycle/cycleDay/allObservations correctly (including a cycle boundary
      // moving, if is_cycle_start just changed), the same way a spouse's edit would.
      queryClient.setQueryData<Database["public"]["Tables"]["observations"]["Row"][]>(
        observationsQueryKey(couple.id),
        (prev) =>
          mergeObservationRow(
            prev,
            savedObs as Database["public"]["Tables"]["observations"]["Row"]
          )
      );

      // A flushed write can land after the user has moved to another day. lastSavedSnapshotRef
      // and the last-saved indicator both describe the day on screen, so only the write for the
      // currently viewed day may update them — otherwise the new day's draft would immediately
      // look dirty and get re-saved for no reason.
      savedSnapshot = getDraftSnapshot(draft);
      if (entryDate === viewedDateRef.current) {
        lastSavedSnapshotRef.current = savedSnapshot;
        emitLastSaved(savedObs?.updated_at || savedObs?.created_at || new Date().toISOString());
      }
      trackEvent("observation_save_succeeded", {
        ...buildObservationEventProps(draft, saveMode),
        isViewingToday,
      });
    } catch (error: unknown) {
      trackEvent("observation_save_failed", {
        ...buildObservationEventProps(draft, saveMode),
        isViewingToday,
      });
      captureException(error, {
        source: "observation_save",
        isViewingToday,
      });
      toast({
        title: "Error",
        description: (error as Error).message,
        variant: "destructive",
      });
    } finally {
      saveInFlightRef.current = false;
      setSaving(false);

      const pendingDraft = pendingSaveDraftRef.current;
      const pendingDate = pendingSaveDateRef.current ?? entryDate;
      if (pendingDraft) {
        pendingSaveDraftRef.current = null;
        pendingSaveDateRef.current = null;
        const pendingSnapshot = getDraftSnapshot(pendingDraft);
        // Compared against what this call actually wrote, not lastSavedSnapshotRef — the queued
        // edit may belong to a different day than the one now on screen.
        if (pendingDate !== entryDate || pendingSnapshot !== savedSnapshot) {
          void saveDraft(pendingDraft, pendingDate);
        }
      }
    }
  }, [
    canWriteObservations,
    couple,
    cycle,
    isViewingToday,
    queryClient,
    toast,
  ]);

  // Writes whatever is currently pending immediately, instead of waiting out (or discarding)
  // the debounce timer. Used by every exit path: day navigation, unmount, tab hidden, blur.
  const flushPendingSave = useCallback(() => {
    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
      autosaveTimeoutRef.current = null;
    }

    const draft = draftRef.current;
    const draftDate = draftDateRef.current;
    if (!draft || !draftDate || !canWriteObservations) return;
    // No resolved couple/cycle means there is nothing a write could be attached to — flushing
    // anyway just trips saveDraft's guard and reports it as an error.
    if (!couple || !cycle) return;
    if (getDraftSnapshot(draft) === lastSavedSnapshotRef.current) return;

    pendingSinceRef.current = null;

    if (saveInFlightRef.current) {
      pendingSaveDraftRef.current = draft;
      pendingSaveDateRef.current = draftDate;
      return;
    }

    void saveDraft(draft, draftDate);
  }, [canWriteObservations, couple, cycle, saveDraft]);

  useEffect(() => {
    flushPendingSaveRef.current = flushPendingSave;
  }, [flushPendingSave]);

  // Nothing typed should die with the page. visibilitychange covers tab switches and iOS/
  // Capacitor backgrounding; pagehide covers reload/close; the cleanup covers leaving the route.
  useEffect(() => {
    const flushOnHide = () => {
      if (document.visibilityState === "hidden") {
        flushPendingSaveRef.current();
      }
    };
    const flushNow = () => flushPendingSaveRef.current();

    document.addEventListener("visibilitychange", flushOnHide);
    window.addEventListener("pagehide", flushNow);

    return () => {
      document.removeEventListener("visibilitychange", flushOnHide);
      window.removeEventListener("pagehide", flushNow);
      flushPendingSaveRef.current();
    };
  }, []);

  useEffect(() => {
    if (loading || !canWriteObservations) return;
    // `loading` only covers the owner-context and cache queries. An account that resolves to
    // no couple at all (session expired mid-session, onboarding not finished) leaves this
    // effect live with a null cycle, where every scheduled save is an empty default draft that
    // saveDraft can only reject and report.
    if (!couple || !cycle) return;
    if (skipNextAutosaveRef.current) {
      skipNextAutosaveRef.current = false;
      return;
    }

    if (saveInFlightRef.current) {
      pendingSaveDraftRef.current = buildDraftFromState();
      pendingSaveDateRef.current = viewedDate;
      return;
    }

    const currentDraft = buildDraftFromState();
    draftRef.current = currentDraft;
    draftDateRef.current = viewedDate;

    const currentSnapshot = getDraftSnapshot(currentDraft);
    if (currentSnapshot === lastSavedSnapshotRef.current) {
      lastAutosaveNotesRef.current = currentDraft.notes;
      pendingSinceRef.current = null;
      return;
    }

    // Typing gets a longer quiet window than tapping, but the run of edits as a whole is still
    // capped: once AUTOSAVE_MAX_WAIT_MS has elapsed since the first unsaved change, the next
    // keystroke saves right away rather than pushing the deadline out again.
    const isTextEdit = currentDraft.notes !== lastAutosaveNotesRef.current;
    lastAutosaveNotesRef.current = currentDraft.notes;

    const now = Date.now();
    if (pendingSinceRef.current === null) {
      pendingSinceRef.current = now;
    }
    const baseDelay = isTextEdit ? AUTOSAVE_TEXT_DEBOUNCE_MS : AUTOSAVE_DEBOUNCE_MS;
    const remainingBeforeMaxWait = AUTOSAVE_MAX_WAIT_MS - (now - pendingSinceRef.current);
    const delay = Math.max(0, Math.min(baseDelay, remainingBeforeMaxWait));

    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
    }

    autosaveTimeoutRef.current = setTimeout(() => {
      autosaveTimeoutRef.current = null;
      if (!draftRef.current || !draftDateRef.current) return;

      const snapshotAtSaveTime = getDraftSnapshot(draftRef.current);
      if (snapshotAtSaveTime === lastSavedSnapshotRef.current) return;

      pendingSinceRef.current = null;

      if (saveInFlightRef.current) {
        pendingSaveDraftRef.current = draftRef.current;
        pendingSaveDateRef.current = draftDateRef.current;
        return;
      }

      void saveDraft(draftRef.current, draftDateRef.current);
    }, delay);

    return () => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }
    };
  }, [
    bleeding,
    isCycleStart,
    monitor,
    ovulationTest,
    intercourse,
    mucus,
    mucusColor,
    mucusFrequency,
    notes,
    symptoms,
    cycleDay,
    buildDraftFromState,
    loading,
    couple,
    canWriteObservations,
    cycle,
    saveDraft,
    viewedDate,
    temperature,
  ]);

  const handleTemperatureChange = (nextValue: string) => {
    setTemperature(nextValue);
  };

  const handleTemperatureBlur = () => {
    if (loading || !canWriteObservations) {
      return;
    }

    const normalized = normalizeTemperatureInput(temperature);
    if (normalized !== temperature) {
      skipNextAutosaveRef.current = true;
      setTemperature(normalized);
    }
    setTemperatureToPersist(normalized);

    const draftToSave = { ...buildDraftFromState(), temperature: normalized };
    draftRef.current = draftToSave;
    draftDateRef.current = viewedDate;

    flushPendingSaveRef.current();
  };

  if (loading) {
    return <LoadingSkeleton />;
  }

  // Determine method-specific UI (union across all selected methods)
  const showExpandedMucus = usesExpandedMucus(couple?.methods ?? []);
  const showMonitor = usesMonitor(couple?.methods ?? []);
  const isSymptoThermal = usesSymptoThermal(couple?.methods ?? []);
  const idleCopy = isSymptoThermal
    ? "Changes save automatically. Temperature saves on blur."
    : "Changes save automatically.";
  const hoverIdleClasses = isTouchOnlyDevice
    ? "border-border"
    : "border-border hover:border-primary/50 hover:bg-accent";
  const readOnlyDisabledClasses =
    "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-border disabled:hover:bg-transparent";

  // Get fertility advice
  const analysis =
    cycle && couple
        ? analyzeCycle(
            cycle,
            allObservations,
            couple.goal,
            couple.methods as NFPMethod[],
            isPostpartum,
            couple.timezone
          )
      : null;

  // Bleeding after this cycle's fertile window closed that looks like the next period: the
  // cycle start was probably never marked. Only prompt — never create a start automatically.
  const likelyNewCycleCd =
    cycle && couple && !cycle.start_inferred
      ? getCyclePhases(cycle, allObservations, {
          methods: couple.methods as NFPMethod[],
          isPostpartum,
        }).likelyNewCycleCd
      : null;
  const likelyNewCycleDate =
    cycle && likelyNewCycleCd !== null
      ? addDays(parseISO(cycle.start_date), likelyNewCycleCd - 1)
      : null;
  const showNewCycleHint =
    likelyNewCycleDate !== null &&
    likelyNewCycleCd !== null &&
    cycleDay >= likelyNewCycleCd &&
    !isCycleStart;

  const adviceDisplay = analysis
    ? getFertilityAdviceDisplay(
        analysis.fertilityAdvice,
        couple!.goal
      )
    : null;

  const BLEEDING_ICON_COLOR: Record<number, string> = {
    4: "text-rose-200",
    1: "text-rose-300",
    2: "text-rose-500",
    3: "text-rose-700",
    5: "text-amber-800",
  };

  const renderMucusConsistencyButtons = (
    options: readonly { value: string; label: string; peak: boolean }[]
  ) => (
    <div className="grid grid-cols-3 gap-2">
      {options.map((item) => (
        <button
          key={item.value}
          onClick={() => setMucus(mucus === item.value ? "" : item.value)}
          disabled={!canWriteObservations}
          className={`flex flex-col items-center justify-center gap-1 p-2 rounded-xl border-2 transition-all touch-manipulation ${
            mucus === item.value
              ? "border-primary bg-primary/10 shadow-md"
              : hoverIdleClasses
          } ${readOnlyDisabledClasses}`}
        >
          <span className="text-xs font-medium text-center">{item.label}</span>
          {item.peak && (
            <span
              className={`text-[10px] font-semibold ${
                mucus === item.value ? "text-primary" : "text-emerald-600 dark:text-emerald-400"
              }`}
            >
              Peak
            </span>
          )}
        </button>
      ))}
    </div>
  );

  // Creighton/Billings teach a detailed, standardized vocabulary (consistency + color as
  // separate observations); Marquette/Sympto-Thermal use mucus as a simpler secondary signal
  // and were never taught this vocabulary, so they keep the original 5-option picker.
  const renderMucusPicker = (variant: "expanded" | "basic") => {
    if (variant === "basic") {
      return renderMucusConsistencyButtons(MUCUS_CONSISTENCY_OPTIONS_BASIC);
    }

    return (
    <>
      {renderMucusConsistencyButtons(MUCUS_CONSISTENCY_OPTIONS)}
      {optionalFields.mucus_color !== false && (
      <div className="space-y-2">
        <p className="text-xs font-medium text-muted-foreground">Color (optional)</p>
        <div className="grid grid-cols-3 gap-2">
          {MUCUS_COLOR_OPTIONS.map((item) => (
            <button
              key={item.value}
              onClick={() => setMucusColor(mucusColor === item.value ? "" : item.value)}
              disabled={!canWriteObservations}
              className={`p-2 rounded-xl border-2 transition-all touch-manipulation text-xs font-medium ${
                mucusColor === item.value
                  ? "border-primary bg-primary/10 shadow-md"
                  : hoverIdleClasses
              } ${readOnlyDisabledClasses}`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
      )}
    </>
    );
  };

  const renderMucusFrequencyPicker = () => (
    <div className="space-y-2">
      <p className="text-xs font-medium text-muted-foreground">
        Frequency (how often was it seen today?)
      </p>
      <div className="grid grid-cols-4 gap-2">
        {MUCUS_FREQUENCY_OPTIONS.map((item) => (
          <button
            key={item.value}
            onClick={() =>
              setMucusFrequency(mucusFrequency === item.value ? "" : item.value)
            }
            disabled={!canWriteObservations}
            className={`p-2 rounded-xl border-2 transition-all touch-manipulation text-xs font-medium ${
              mucusFrequency === item.value
                ? "border-primary bg-primary/10 shadow-md"
                : hoverIdleClasses
            } ${readOnlyDisabledClasses}`}
          >
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <SubscriptionGate
      coupleId={couple?.id}
      readOnlyMessage="Subscribe to continue tracking your daily observations and cycles."
    >
      <div className="space-y-4">
        {/* Welcome Guide */}
        {showWelcome && <WelcomeGuide onDismiss={dismissWelcome} />}

        {/* Getting-started checklist nudge */}
        <GettingStartedCard />

        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-background/70 px-4 py-3">
            <div>
              <p className="text-sm text-muted-foreground">Current cycle status</p>
              {cycle?.start_inferred ? (
                <>
                  <p className="text-lg font-semibold">Before your first recorded cycle</p>
                  <p className="text-xs text-muted-foreground">
                    If this is the first day of your period, turn on Cycle start.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-lg font-semibold">Cycle Day {cycleDay}</p>
                  {showNewCycleHint && likelyNewCycleDate && (
                    <p className="text-xs text-muted-foreground">
                      Looks like a new cycle started on {format(likelyNewCycleDate, "MMM d")} — mark
                      Cycle start on that day.
                    </p>
                  )}
                </>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-2 rounded-md border px-3 py-1.5">
                <Switch
                  id="cycle-start-toggle"
                  checked={isCycleStart}
                  onCheckedChange={setIsCycleStart}
                  disabled={!canWriteObservations}
                  aria-label="Mark this observation as cycle start"
                />
                <Label htmlFor="cycle-start-toggle" className="text-sm font-medium">
                  Cycle start
                </Label>
              </div>
            </div>
          </div>

          {!canWriteObservations && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-900 dark:text-amber-100">
              Your access is read-only. Tracking fields are disabled.
            </div>
          )}

          <Card className="p-6 space-y-6 shadow-[var(--shadow-soft)]">
            {/* Mucus Section - Primary for Creighton & Billings */}
            {showExpandedMucus && optionalFields.mucus !== false && (
              <div className="space-y-4">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Droplets className="h-5 w-5 text-emerald-500" />
                  Mucus Observation
                </Label>
                {renderMucusPicker("expanded")}
                {optionalFields.mucus_frequency === true && renderMucusFrequencyPicker()}
              </div>
            )}

            {/* Temperature Section - Prominent for Sympto-Thermal */}
            {isSymptoThermal && optionalFields.temperature !== false && (
              <div className="space-y-4">
                <Label htmlFor="temperature-st" className="text-base font-semibold flex items-center gap-2">
                  <Thermometer className="h-5 w-5 text-blue-500" />
                  Basal Body Temperature (°F)
                </Label>
                <div className="flex items-center gap-4">
                  <Input
                    id="temperature-st"
                    type="text"
                    inputMode="decimal"
                    value={temperature}
                    onChange={(e) => handleTemperatureChange(e.target.value)}
                    onBlur={handleTemperatureBlur}
                    disabled={!canWriteObservations}
                    placeholder="e.g., 97.83"
                    className="text-lg h-14"
                  />
                  <span className="text-sm text-muted-foreground">
                    Basal body temp
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Take temperature immediately upon waking, before getting out
                  of bed or any activity. Record at the same time each day.
                </p>
              </div>
            )}

            {/* Monitor Section - shown when any selected method uses a monitor */}
            {showMonitor &&
              optionalFields.monitor !== false && (
                <div className="space-y-4">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <Activity className="h-5 w-5 text-blue-500" />
                    Monitor
                  </Label>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      {
                        value: "LOW" as const,
                        label: "Low",
                        icon: Activity,
                        color: "text-blue-400",
                      },
                      {
                        value: "HIGH" as const,
                        label: "High",
                        icon: Zap,
                        color: "text-amber-500",
                      },
                      {
                        value: "PEAK" as const,
                        label: "Peak",
                        icon: Thermometer,
                        color: "text-rose-500",
                      },
                    ].map((item) => (
                      <button
                        key={item.value}
                        onClick={() =>
                          setMonitor(monitor === item.value ? null : item.value)
                        }
                        disabled={!canWriteObservations}
                        className={`flex flex-col items-center justify-center gap-3 p-4 rounded-xl border-2 transition-all touch-manipulation ${
                          monitor === item.value
                            ? "border-primary bg-primary/10 shadow-md"
                            : hoverIdleClasses
                        } ${readOnlyDisabledClasses}`}
                      >
                        <item.icon
                          className={`h-8 w-8 ${
                            monitor === item.value ? "text-primary" : item.color
                          }`}
                        />
                        <span className="text-sm font-medium capitalize">
                          {item.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

            {/* Ovulation test (OPK) - optional, off by default */}
            {optionalFields.ovulation_test === true && (
              <div className="space-y-4">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <FlaskConical className="h-5 w-5 text-violet-500" />
                  Ovulation test
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  {(
                    [
                      { value: "positive", label: "Positive" },
                      { value: "negative", label: "Negative" },
                    ] as const
                  ).map((item) => (
                    <button
                      key={item.value}
                      type="button"
                      aria-pressed={ovulationTest === item.value}
                      onClick={() =>
                        setOvulationTest((current) =>
                          current === item.value ? null : item.value
                        )
                      }
                      disabled={!canWriteObservations}
                      className={`flex items-center justify-center gap-2 p-4 rounded-xl border-2 transition-all touch-manipulation ${
                        ovulationTest === item.value
                          ? "border-primary bg-primary/10 shadow-md"
                          : hoverIdleClasses
                      } ${readOnlyDisabledClasses}`}
                    >
                      <span className="text-base font-medium">{item.label}</span>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  Tap again to clear (not tested). A positive result marks the LH surge, which
                  usually precedes ovulation by about a day.
                </p>
              </div>
            )}

            {/* Temperature Section - For non-Sympto-Thermal methods */}
            {!isSymptoThermal && optionalFields.temperature !== false && (
              <div className="space-y-4">
                <Label
                  htmlFor="temperature"
                  className="text-base font-semibold flex items-center gap-2"
                >
                  <Thermometer className="h-5 w-5 text-blue-500" />
                  Temperature (°F)
                </Label>
                <div className="flex items-center gap-4">
                  <Input
                    id="temperature"
                    type="text"
                    inputMode="decimal"
                    value={temperature}
                    onChange={(e) => handleTemperatureChange(e.target.value)}
                    onBlur={handleTemperatureBlur}
                    disabled={!canWriteObservations}
                    placeholder="e.g., 97.83"
                    className="text-lg h-14"
                  />
                  <span className="text-sm text-muted-foreground">
                    Basal body temp
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Take temperature first thing in morning before getting out of
                  bed
                </p>
              </div>
            )}

            {/* Bleeding Section */}
            {optionalFields.bleeding !== false && (
              <div className="space-y-4">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Droplets className="h-5 w-5 text-rose-500" />
                  Bleeding
                </Label>
                <div className="grid grid-cols-3 gap-3">
                  {BLEEDING_OPTIONS.map((item) => (
                    <button
                      key={item.value}
                      onClick={() =>
                        setBleeding(bleeding === item.value ? null : item.value)
                      }
                      disabled={!canWriteObservations}
                      className={`flex flex-col items-center justify-center gap-3 p-4 rounded-xl border-2 transition-all touch-manipulation ${
                        bleeding === item.value
                          ? "border-primary bg-primary/10 shadow-md"
                          : hoverIdleClasses
                      } ${readOnlyDisabledClasses}`}
                    >
                      <Droplets
                        className={`h-8 w-8 ${
                          bleeding === item.value ? "text-primary" : BLEEDING_ICON_COLOR[item.value]
                        }`}
                      />
                      <span className="text-sm font-medium">{item.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Mucus Section - For non-Creighton/Billings methods */}
            {!showExpandedMucus && optionalFields.mucus !== false && (
              <div className="space-y-4">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Droplets className="h-5 w-5 text-emerald-500" />
                  Mucus
                </Label>
                {renderMucusPicker("basic")}
                {optionalFields.mucus_frequency === true && renderMucusFrequencyPicker()}
              </div>
            )}

            {/* Intercourse Section */}
            <div className="space-y-4">
              <Label className="text-base font-semibold flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-500" />
                Intercourse
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() =>
                    setIntercourse((current) => (current === true ? null : true))
                  }
                  disabled={!canWriteObservations}
                  className={`flex items-center justify-center gap-2 p-4 rounded-xl border-2 transition-all touch-manipulation ${
                    intercourse === true
                      ? "border-primary bg-primary/10 shadow-md"
                      : hoverIdleClasses
                  } ${readOnlyDisabledClasses}`}
                >
                  <span className="text-base font-medium">Yes</span>
                </button>
                <button
                  onClick={() =>
                    setIntercourse((current) => (current === false ? null : false))
                  }
                  disabled={!canWriteObservations}
                  className={`flex items-center justify-center gap-2 p-4 rounded-xl border-2 transition-all touch-manipulation ${
                    intercourse === false
                      ? "border-primary bg-primary/10 shadow-md"
                      : hoverIdleClasses
                  } ${readOnlyDisabledClasses}`}
                >
                  <span className="text-base font-medium">No</span>
                </button>
              </div>
            </div>

            {/* Symptoms Section */}
            {optionalFields.symptoms !== false && (
              <div className="space-y-4">
                <Label className="text-base font-semibold">
                  Symptoms
                </Label>
                <div className="grid grid-cols-3 gap-3">
                  {SYMPTOM_OPTIONS.map((symptom) => (
                    <button
                      key={symptom.name}
                      onClick={() => {
                        if (symptoms.includes(symptom.name)) {
                          setSymptoms(
                            symptoms.filter((s) => s !== symptom.name)
                          );
                        } else {
                          setSymptoms([...symptoms, symptom.name]);
                        }
                      }}
                      disabled={!canWriteObservations}
                      className={`flex flex-col items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all touch-manipulation ${
                        symptoms.includes(symptom.name)
                          ? "border-primary bg-primary/10 shadow-md"
                          : hoverIdleClasses
                      } ${readOnlyDisabledClasses}`}
                    >
                      <symptom.icon
                        className={`h-6 w-6 ${
                          symptoms.includes(symptom.name)
                            ? "text-primary"
                            : symptom.color
                        }`}
                      />
                      <span className="text-xs font-medium text-center">
                        {symptom.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Notes Section */}
            {optionalFields.notes !== false && (
              <div className="space-y-4">
                <Label
                  htmlFor="notes"
                  className="text-base font-semibold flex items-center gap-2"
                >
                  <Coffee className="h-5 w-5 text-amber-600" />
                  Additional Notes
                </Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  onBlur={() => flushPendingSaveRef.current()}
                  disabled={!canWriteObservations}
                  placeholder={`Any other observations or notes for ${isViewingToday ? "today" : "this day"}...`}
                  className="min-h-[100px] resize-none"
                />
              </div>
            )}

            <p className="text-sm text-muted-foreground text-center" role="status" aria-live="polite">
              {saving ? "Saving changes..." : idleCopy}
            </p>
          </Card>

          {/* Tracking Guide at Bottom */}
          <div className="flex justify-center pb-8 pt-4">
            <TrackingGuide
              isPostpartum={isPostpartum}
              methods={couple?.methods}
              showOvulationTest={optionalFields.ovulation_test === true}
            />
          </div>
        </div>

      </div>
    </SubscriptionGate>
  );
};

export default Observations;
